import React, { useEffect, useState } from "react";
import { ethers } from "ethers";
import BerryPatchAbi from "./BerryPatchAbi.json";
import './App.css';
import './Forest.css';
import './Pools.css';

import ForestBackground from './ForestBackground';

const contractAddress = "0xC83937763df8050f5F1d7A75f79b5be22696D957";

const pools = [
  { pid: 0, token: "0xccea61b327f074d777ebb9affdaf5581bca10918", isNFT: true, rewardRate: "100", name: "Derpe Beras" },
  { pid: 1, token: "0x2a7f10680a8c3b8a8a6d409fe464324d39660410", isNFT: false, rewardRate: "1", name: "SAMLON" },
  { pid: 2, token: "0x5772FeB023293edA16F853Bb058132b93AF239f4", isNFT: false, rewardRate: "2", name: "BONES" },
];

const TOKEN_ADDRESS = "0x2a7f10680a8c3b8a8a6d409fe464324d39660410";
const DERPE_BERA_ADDRESS = "0xccea61b327f074d777ebb9affdaf5581bca10918";
const BONES_ADDRESS = "0x5772FeB023293edA16F853Bb058132b93AF239f4";
const BERRY_ADDRESS = "0x82EB46aFa4C449331E20Ed04476C21e3813a5BaE";

function InputModal({ isOpen, title, label, onClose, onConfirm, type = "deposit" }) {
  const [value, setValue] = useState("");

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>{title}</h2>
        <label style={{ display: "block", marginBottom: "5px" }}>{label}</label>
        <input
          type="number"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          style={{ width: "100%", marginBottom: "10px", padding: "8px" }}
        />
        <div className="modal-buttons">
          <button 
            onClick={() => { onConfirm(value); setValue(""); }} 
            style={{ marginRight: "10px" }}
            className={type === "withdraw" ? "withdraw-btn" : "deposit-btn"}
          >
            Confirm
          </button>
          <button onClick={() => { onClose(); setValue(""); }}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function TransactionPendingModal({ isOpen, message }) {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2>Transaction Pending</h2>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <div className="loading-spinner" style={{ margin: '0 auto 20px' }}></div>
          <p>{message}</p>
        </div>
      </div>
    </div>
  );
}

function App() {
  const [provider, setProvider] = useState(null);
  const [account, setAccount] = useState(null);
  const [pending, setPending] = useState({});
  const [userBalance, setUserBalance] = useState(null);
  const [beraBalance, setBeraBalance] = useState(null);
  const [ownedBeras, setOwnedBeras] = useState([]);
  const [isLoadingBeras, setIsLoadingBeras] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [bonesBalance, setBonesBalance] = useState(null);
  const [modalLabel, setModalLabel] = useState("");
  const [userDeposits, setUserDeposits] = useState({});
  const [userNFTDeposits, setUserNFTDeposits] = useState({});
  const [depositModalOpen, setDepositModalOpen] = useState(false);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [currentPid, setCurrentPid] = useState(null);
  const [berryBalance, setBerryBalance] = useState(0);
  const [transactionPending, setTransactionPending] = useState(false);
  const [transactionMessage, setTransactionMessage] = useState("");
  const [leaderboard, setLeaderboard] = useState([]);


  // Initialize provider on mount
  useEffect(() => {
    if (window.ethereum) {
      const prov = new ethers.BrowserProvider(window.ethereum);
      setProvider(prov);
    }
  }, []);

  // Create the main contract instance
  const contract = provider ? new ethers.Contract(contractAddress, BerryPatchAbi, provider) : null;

  // Fetch user balances and deposits
  const fetchUserBalance = async (walletAddress) => {
    if (!provider) return;
    try {
      const token = new ethers.Contract(TOKEN_ADDRESS, ["function balanceOf(address) view returns (uint256)"], provider);
      const balance = await token.balanceOf(walletAddress);
      setUserBalance(Number(ethers.formatUnits(balance, 18)));
    } catch (err) {
      console.error("Failed to fetch user balance:", err);
    }
  };

  const fetchBonesBalance = async (walletAddress) => {
    if (!provider) return;
    try {
      const bones = new ethers.Contract(BONES_ADDRESS, ["function balanceOf(address) view returns (uint256)"], provider);
      const balance = await bones.balanceOf(walletAddress);
      setBonesBalance(Number(ethers.formatUnits(balance, 18)));
    } catch (err) {
      console.error("Failed to fetch BONES balance:", err);
    }
  };

  const fetchBerryBalance = async (walletAddress) => {
    if (!provider) return;
    try {
      const berries = new ethers.Contract(BERRY_ADDRESS, ["function balanceOf(address) view returns (uint256)"], provider);
      const balance = await berries.balanceOf(walletAddress);
      setBerryBalance(Number(ethers.formatUnits(balance, 18)));
    } catch (err) {
      console.error("Failed to fetch BERRY balance:", err);
    }
  };

  const fetchLeaderboard = async () => {
    if (!provider) return;
    try {
      const berriesContract = new ethers.Contract(BERRY_ADDRESS, [
        "function getHoldersByBalance() view returns (address[] memory, uint256[] memory)"
      ], provider);
  
      const [addresses, balances] = await berriesContract.getHoldersByBalance();
  
      const formatted = addresses.map((addr, i) => ({
        address: addr,
        balance: Number(ethers.formatUnits(balances[i], 18))
      }));
  
      // Sort descending just in case the contract doesn’t already
      formatted.sort((a, b) => b.balance - a.balance);
  
      setLeaderboard(formatted);
    } catch (err) {
      console.error("Failed to fetch leaderboard:", err);
    }
  };
  

  const fetchDerpeBeras = async (walletAddress) => {
    if (!walletAddress || !provider) return;
    setIsLoadingBeras(true);
    try {
      const nftContract = new ethers.Contract(DERPE_BERA_ADDRESS, [
        "function balanceOf(address) view returns (uint256)",
        "function tokenOfOwnerByIndex(address, uint256) view returns (uint256)"
      ], provider);

      const balance = await nftContract.balanceOf(walletAddress);
      setBeraBalance(Number(balance));

      const ownedTokens = [];
      for (let i = 0; i < Number(balance); i++) {
        const tokenId = await nftContract.tokenOfOwnerByIndex(walletAddress, i);
        ownedTokens.push(Number(tokenId));
      }
      setOwnedBeras(ownedTokens);
    } catch (err) {
      console.error("Failed to fetch Derpe Beras:", err);
    } finally {
      setIsLoadingBeras(false);
    }
  };

  const fetchUserDeposit = async (pid, account) => {
    try {
      const info = await contract.users(pid, account);
      setUserDeposits(prev => ({ ...prev, [pid]: Number(ethers.formatUnits(info.amount, 18)) }));
    } catch (err) {
      console.error(`Failed to fetch user deposit for pool ${pid}:`, err);
    }
  };

  const fetchUserNFTDeposits = async (pid, account) => {
    console.log("fetchingUserNFTDeposits", contract, account);
    
    try {
      const depositedIds = await contract.getUserNFTs(pid, account);
      console.log(depositedIds);
      setUserNFTDeposits(prev => ({
        ...prev,
        [pid]: depositedIds.map(id => Number(id))
      }));
    } catch (err) {
      console.error(`Failed to fetch NFT deposits for pool ${pid}:`, err);
      setUserNFTDeposits(prev => ({ ...prev, [pid]: [] }));
    }
  };

  const connectWallet = async () => {
    if (!window.ethereum) return alert("Please install MetaMask");
    setIsConnecting(true);
    try {
      const browserProvider = new ethers.BrowserProvider(window.ethereum);
      const accounts = await browserProvider.send("eth_requestAccounts", []);
      setAccount(accounts[0]);

      await fetchUserBalance(accounts[0]);
      await fetchBonesBalance(accounts[0]);
      await fetchDerpeBeras(accounts[0]);
      await fetchBerryBalance(accounts[0])
      await fetchLeaderboard();

      await Promise.all(
        pools.map(async (p) => {
          await fetchUserDeposit(p.pid, accounts[0]);
          if (p.isNFT) await fetchUserNFTDeposits(p.pid, accounts[0]);
        })
      );
    } catch (err) {
      console.error("Wallet connection failed:", err);
      alert("Failed to connect wallet. Make sure your network is correct.");
    } finally {
      setIsConnecting(false);
    }
  };

  // Deposit workflow
  const deposit = (pid) => {
    const pool = pools.find(p => p.pid === pid);
    setCurrentPid(pid);
    setModalLabel(pool.isNFT ? "Enter tokenId" : "Enter amount");
    setDepositModalOpen(true);
  };

  useEffect(() => {
    if (!account) return;
    fetchLeaderboard();
    const interval = setInterval(fetchLeaderboard, 15000); // refresh every 15s
    return () => clearInterval(interval);
  }, [account, provider]);
  

  // Withdraw workflow
  const withdraw = (pid) => {
    const pool = pools.find(p => p.pid === pid);
    setCurrentPid(pid);
    setModalLabel(pool.isNFT ? "Enter tokenId to withdraw" : "Enter amount to withdraw");
    setWithdrawModalOpen(true);
  };

  const handleDepositConfirm = async (value) => {
    setDepositModalOpen(false);
    if (!value || isNaN(value) || Number(value) <= 0) return;
  
    try {
      if (!provider) throw new Error("Provider not initialized");
  
      const pool = pools.find(p => p.pid === currentPid);
      const signer = await provider.getSigner();
  
      // Contract with signer
      const contractWithSigner = new ethers.Contract(contractAddress, BerryPatchAbi, signer);
  
      setTransactionPending(true);
      setTransactionMessage("Processing deposit...");
  
      if (!pool.isNFT) {
        // ERC20 deposit
        const tokenContract = new ethers.Contract(pool.token, [
          "function allowance(address owner, address spender) view returns (uint256)",
          "function approve(address spender, uint256 amount) returns (bool)"
        ], signer);
  
        const amount = ethers.parseUnits(value, 18);
  
        // Check allowance
        const allowance = await tokenContract.allowance(account, contractAddress);
        if (allowance < amount) {
          setTransactionMessage("Approving tokens...");
          const approveTx = await tokenContract.approve(contractAddress, amount);
          await approveTx.wait();
          console.log(`Approved ${amount} tokens for pool ${pool.name}`);
        }
  
        setTransactionMessage("Depositing tokens...");
        const tx = await contractWithSigner.deposit(currentPid, amount);
        await tx.wait();
  
      } else {
        // NFT deposit
        const nftContract = new ethers.Contract(pool.token, [
          "function isApprovedForAll(address owner, address operator) view returns (bool)",
          "function setApprovalForAll(address operator, bool approved)"
        ], signer);
  
        const tokenId = Number(value);
  
        const isApproved = await nftContract.isApprovedForAll(account, contractAddress);
        if (!isApproved) {
          setTransactionMessage("Approving NFT...");
          const approveTx = await nftContract.setApprovalForAll(contractAddress, true);
          await approveTx.wait();
          console.log(`Approved all NFTs for pool ${pool.name}`);
        }
  
        setTransactionMessage("Depositing NFT...");
        const tx = await contractWithSigner.deposit(currentPid, tokenId);
        await tx.wait();
      }
  
      setTransactionPending(false);
      alert("Deposit successful!");
      
      // Refresh data
      await fetchUserDeposit(currentPid, account);
      await checkPending(currentPid);
      if (pool.isNFT) await fetchUserNFTDeposits(currentPid, account);
  
    } catch (err) {
      console.error(err);
      setTransactionPending(false);
      alert("Deposit failed!");
    }
  };

  const handleWithdrawConfirm = async (value) => {
    setWithdrawModalOpen(false);
    if (!value || isNaN(value) || Number(value) <= 0) return;
  
    try {
      const signer = await provider.getSigner();
      const contractWithSigner = new ethers.Contract(contractAddress, BerryPatchAbi, signer);
      const pool = pools.find(p => p.pid === currentPid);
  
      setTransactionPending(true);
      setTransactionMessage("Processing withdrawal...");
  
      const tx = await contractWithSigner.withdraw(currentPid, value);
      await tx.wait();
  
      setTransactionPending(false);
      alert("Withdraw successful!");
      
      // Refresh data
      await fetchUserDeposit(currentPid, account);
      await checkPending(currentPid);
      if (pool.isNFT) await fetchUserNFTDeposits(currentPid, account);
  
    } catch (err) {
      console.error(err);
      setTransactionPending(false);
      alert("Withdraw failed!");
    }
  };

  const harvest = async (pid) => {
    try {
      const signer = await provider.getSigner();
      const contractWithSigner = new ethers.Contract(contractAddress, BerryPatchAbi, signer);
      
      setTransactionPending(true);
      setTransactionMessage("Harvesting berries...");
      
      const tx = await contractWithSigner.harvest(pid);
      await tx.wait();
      
      setTransactionPending(false);
      alert("Harvest successful!");
      await checkPending(pid);
      await fetchBerryBalance(account);
      
    } catch (err) {
      console.error(err);
      setTransactionPending(false);
      alert("Harvest failed!");
    }
  };

  const checkPending = async (pid) => {
    try {
      const amount = await contract.pendingBerries(pid, account);
      setPending(prev => ({ ...prev, [pid]: ethers.formatUnits(amount, 18) }));
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (!account || !contract) return;
    
    pools.forEach(p => {
      const depositAmount = userDeposits[p.pid] || (p.isNFT ? (userNFTDeposits[p.pid]?.length || 0) : 0);
      if (depositAmount > 0) {
        checkPending(p.pid);
      }
    });
    
    const interval = setInterval(() => {
      pools.forEach(p => {
        const depositAmount = userDeposits[p.pid] || (p.isNFT ? (userNFTDeposits[p.pid]?.length || 0) : 0);
        if (depositAmount > 0) {
          checkPending(p.pid);
        }
      });
    }, 10000); // every 10 seconds
  
    return () => clearInterval(interval);
  }, [account, contract, userDeposits, userNFTDeposits]);

  // Auto-refresh Berry balance every 10 seconds and on load
  useEffect(() => {
    if (!account) return;

    // Run once immediately
    fetchBerryBalance(account);

    // Then run every 10 seconds
    const interval = setInterval(() => {
      fetchBerryBalance(account);
    }, 10000);

    // Cleanup on unmount
    return () => clearInterval(interval);
  }, [account, provider]);

  return (
    <div className="app-container">
      <ForestBackground />
      
      {!account ? (
        <div className="lander">
          <h1>Welcome to the Berry Patch</h1>
          <button className="connect-btn" onClick={connectWallet}>
            {isConnecting ? <span className="loading"></span> : 'Connect'}
          </button>
        </div>
      ) : (
        <div>
          <div className="account-info">
            <div className="account-address">
              Connected: {account.slice(0, 6)}...{account.slice(-4)}
            </div>
            <div className="berry-balance">
              Berry Balance: {berryBalance.toFixed(4)}
            </div>
          </div>
          
          <div className="pools">
            {pools.map(p => {
              const balance = p.isNFT ? ownedBeras.length : (p.pid === 1 ? userBalance : bonesBalance);
              const depositAmount = p.isNFT ? userNFTDeposits[p.pid]?.length || 0 : userDeposits[p.pid] || 0;
              const pendingRewards = parseFloat(pending[p.pid] || 0);
              const progress = Math.min((pendingRewards / 10) * 100, 100);

              return (
                <div key={p.pid} className="pool-card" data-pool={p.pid}>
                  {/* Decorations */}
                  <div className="leaf-decoration"></div>
                  
                  <div className="pool-header">
                    <div className="pool-title">
                      <div className="pool-icon">
                        {p.isNFT ? '🖼️' : p.pid === 1 ? '🐟' : '🦴'}
                      </div>
                      {p.name}
                    </div>
                    <div className="pool-reward-rate">
                      +{p.rewardRate} 🍓/day
                    </div>
                  </div>

                  <div className="pool-stats">
                    <div className="stat">
                      <div className="stat-label">
                        <span>🌿 Your Balance</span>
                      </div>
                      <div className="stat-value">
                        {balance} {p.isNFT && ownedBeras.length > 0 && ` (#${ownedBeras.join(', #')})`}
                      </div>
                    </div>

                    <div className="stat">
                      <div className="stat-label">
                        <span>📥 Your Deposit</span>
                      </div>
                      <div className="stat-value">
                        {depositAmount}
                        {p.isNFT && userNFTDeposits[p.pid]?.length > 0 && ` (#${userNFTDeposits[p.pid].join(', #')})`}
                      </div>
                    </div>

                    <div className="stat">
                      <div className="stat-label">
                        <span>⏳ Pending Rewards</span>
                      </div>
                      <div className="stat-value">
                        {pendingRewards.toFixed(4)} 🍓
                      </div>
                    </div>

                    <div className="stat">
                      <div className="stat-label">
                        <span>Your Daily Rate</span>
                      </div>
                      <div className="stat-value">
                        {depositAmount * p.rewardRate}🍓 
                      </div>
                    </div>
                  </div>

                  {/* NFT Deposit Info */}
                  {p.isNFT && userNFTDeposits[p.pid]?.length > 0 && (
                    <div className="nft-deposit">
                      <div className="stat-label">
                        <span>🎨 Deposited NFTs</span>
                      </div>
                      <div className="nft-list">
                        {userNFTDeposits[p.pid].map(tokenId => (
                          <span key={tokenId} className="nft-token">#{tokenId}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="pool-actions">
                    <button 
                      className="action-btn deposit"
                      onClick={() => deposit(p.pid)}
                    >
                      🌱 Deposit
                    </button>
                    <button 
                      className="action-btn withdraw"
                      onClick={() => withdraw(p.pid)}
                    >
                      🍂 Withdraw
                    </button>
                    <button 
                      className="action-btn harvest"
                      onClick={() => harvest(p.pid)}
                      disabled={pendingRewards <= 0}
                    >
                      🍓 Harvest
                    </button>
                  </div>
                </div>
              );
            })}
            {/* Leaderboard card */}
            <div className="pool-card leaderboard-card">
              <div className="leaf-decoration"></div>
              <div className="pool-header">
                <div className="pool-title">
                  🏆 Berry Leaderboard
                </div>
              </div>

              <div className="leaderboard-content">
                {leaderboard.length === 0 ? (
                  <div className="stat-value">Loading...</div>
                ) : (
                  <table className="leaderboard-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Address</th>
                        <th>🍓 Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaderboard.slice(0, 10).map((entry, idx) => (
                        <tr key={entry.address}>
                          <td>{idx + 1}</td>
                          <td>{entry.address.slice(0, 6)}...{entry.address.slice(-4)}</td>
                          <td>{entry.balance.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

          </div>
          
          <InputModal
            isOpen={depositModalOpen}
            title="Deposit"
            label={modalLabel}
            onClose={() => setDepositModalOpen(false)}
            onConfirm={handleDepositConfirm}
            type="deposit"
          />

          <InputModal
            isOpen={withdrawModalOpen}
            title="Withdraw"
            label={modalLabel}
            onClose={() => setWithdrawModalOpen(false)}
            onConfirm={handleWithdrawConfirm}
            type="withdraw"
          />

          <TransactionPendingModal
            isOpen={transactionPending}
            message={transactionMessage}
          />
        </div>
      )}
    </div>
  );
}

export default App;